import React, { useState, useEffect, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { playSound } from '../../shared/soundEngine';
import { s_TI } from '../../shared/styles';
import { getBusiness, updateBusiness, getBusinessByUser, deleteBusiness } from './businessApi';
import { INDUSTRIES } from './BusinessDashboard';
import BusinessSetupScreen from './BusinessSetupScreen';
import BusinessDashboard from './BusinessDashboard';
import BusinessAgendaScreen from './BusinessAgendaScreen';
import BusinessProfileScreen from './BusinessProfileScreen';
import BusinessMembersScreen from './BusinessMembersScreen';
import BusinessPulseScreen from './BusinessPulseScreen';
import BusinessReportsScreen from './BusinessReportsScreen';
import BusinessSettingsScreen from './BusinessSettingsScreen';
import BusinessROIScreen from './BusinessROIScreen';
import BusinessRIEScreen from './BusinessRIEScreen';
import BusinessProgramsScreen from './BusinessProgramsScreen';
import BusinessServicesScreen from './BusinessServicesScreen';
import BusinessMemberProfileScreen from './BusinessMemberProfileScreen';
import BusinessHRScreen from './BusinessHRScreen';
import BusinessFeedScreen from './BusinessFeedScreen';
import BusinessTrainingScreen from './BusinessTrainingScreen';
import BusinessActivityScreen from './BusinessActivityScreen';
import BusinessIntroModal, { BIZ_TOUR_KEY } from './BusinessIntroModal';
import TechLoadingBar from '../../components/ui/TechLoadingBar';
import BusinessCustomersScreen from './BusinessCustomersScreen';
import BusinessBookingsScreen from './BusinessBookingsScreen';
import BusinessBookingWidget from './BusinessBookingWidget';
import BusinessProductsScreen from './BusinessProductsScreen';
import BusinessOrdersScreen from './BusinessOrdersScreen';
import BusinessApiScreen from './BusinessApiScreen';
import BusinessTasksScreen from './BusinessTasksScreen';
import BusinessProjectsScreen from './BusinessProjectsScreen';
import BusinessImportScreen from './BusinessImportScreen';
import BusinessAdministrationScreen from './BusinessAdministrationScreen';
import BusinessOwnershipTransferScreen from './BusinessOwnershipTransferScreen';
import BusinessManualScreen from './BusinessManualScreen';
import BusinessLicensePackagesScreen from './BusinessLicensePackagesScreen';

const BASE = process.env.REACT_APP_API_URL || '/api';

// Bottom navigation sections
const BOTTOM_NAV = [
  { id: 'home',     icon: '🏠', nl: 'Home',      en: 'Home' },
  { id: 'clients',  icon: '🤝', nl: 'Klanten',   en: 'Clients' },
  { id: 'team',     icon: '👥', nl: 'Team',       en: 'Team' },
  { id: 'wellness', icon: '💚', nl: 'Welzijn',    en: 'Wellness' },
  { id: 'insights', icon: '📊', nl: 'Inzichten',  en: 'Insights' },
  { id: 'hr',       icon: '📋', nl: 'HR',         en: 'HR' },
  { id: 'admin',    icon: '🧾', nl: 'Admin',      en: 'Admin' },
  { id: 'more',     icon: '⚙️', nl: 'Meer',       en: 'More' },
];

// Sub-tabs per section (role-filtered later)
const SECTION_TABS = {
  home:     [
    { id: 'dashboard', nl: 'Dashboard', en: 'Dashboard', roles: ['owner','admin','manager','hr','member'] },
    { id: 'feed',      nl: 'Team feed', en: 'Team feed', roles: ['owner','admin','manager','hr','member','accountant','bookkeeper','hr_manager'] },
    { id: 'agenda',    nl: 'Agenda',    en: 'Agenda',    roles: ['owner','admin','manager','hr','member'] },
    { id: 'manual',    nl: 'Handleiding', en: 'Manual',  roles: ['owner','admin','manager','hr','member','accountant','bookkeeper','hr_manager'] },
  ],
  clients:  [
    { id: 'customers', nl: 'CRM',          en: 'CRM',          roles: ['owner','admin','manager'] },
    { id: 'bookings',  nl: 'Boekingen',    en: 'Bookings',     roles: ['owner','admin','manager'] },
    { id: 'services',  nl: 'Diensten',     en: 'Services',     roles: ['owner','admin','manager'] },
    { id: 'products',  nl: 'Producten',    en: 'Products',     roles: ['owner','admin','manager'] },
    { id: 'orders',    nl: 'Bestellingen', en: 'Orders',       roles: ['owner','admin','manager','accountant'] },
    { id: 'licenses',  nl: 'Licenties',   en: 'Licences',     roles: ['owner','admin','manager','accountant'], industry: 'software' },
    { id: 'bizapi',    nl: 'API',          en: 'API',          roles: ['owner','admin'] },
    { id: 'widget',    nl: 'Widget',       en: 'Widget',       roles: ['owner','admin'] },
    { id: 'import',    nl: 'Import',       en: 'Import',       roles: ['owner','admin'] },
  ],
  team:     [
    { id: 'members',  nl: 'Team',        en: 'Team',       roles: ['owner','admin','manager','hr','member'] },
    { id: 'tasks',    nl: 'Taken',       en: 'Tasks',      roles: ['owner','admin','manager','hr','member'] },
    { id: 'projects', nl: "Projecten",   en: 'Projects',   roles: ['owner','admin','manager'] },
  ],
  wellness: [
    { id: 'programs', nl: "Programma's", en: "Programs",  roles: ['owner','admin','manager','member'] },
    { id: 'training', nl: 'Training',    en: 'Training',  roles: ['owner','admin','manager','member'] },
    { id: 'activity', nl: 'Activiteit',  en: 'Activity',  roles: ['owner','admin','manager','hr','member'] },
  ],
  insights: [
    { id: 'pulse',    nl: 'Pulse',       en: 'Pulse',      roles: ['owner','admin','manager','hr'] },
    { id: 'reports',  nl: 'Rapporten',   en: 'Reports',    roles: ['owner','admin','manager','hr'] },
    { id: 'roi',      nl: 'ROI',         en: 'ROI',        roles: ['owner','admin','hr'] },
    { id: 'rie',      nl: 'RI&E / eNPS', en: 'RI&E / eNPS',roles: ['owner','admin','hr'] },
  ],
  hr: [
    { id: 'contracts', nl: 'Contracten',    en: 'Contracts',    roles: ['owner','admin','accountant','bookkeeper','hr_manager'] },
    { id: 'leave',     nl: 'Verlof',        en: 'Leave',        roles: ['owner','admin','manager','hr','member','accountant','bookkeeper','hr_manager'] },
    { id: 'sick',      nl: 'Ziekmeldingen', en: 'Sick reports', roles: ['owner','admin','manager','hr','member','accountant','bookkeeper','hr_manager'] },
    { id: 'balance',   nl: 'Verlofsaldo',   en: 'Balance',      roles: ['owner','admin','accountant','bookkeeper','hr_manager'] },
    { id: 'hrroles',   nl: 'HR-rollen',     en: 'HR roles',     roles: ['owner','admin'] },
  ],
  admin:    [
    { id: 'administration', nl: 'Administratie', en: 'Administration', roles: ['owner','admin','accountant','bookkeeper'] },
    { id: 'transfer',       nl: 'Overdracht',    en: 'Transfer',       roles: ['owner'] },
  ],
  more:     [
    { id: 'profile',  nl: 'Profiel',     en: 'Profile',    roles: ['owner','admin','manager','hr','member'] },
    { id: 'settings', nl: 'Instellingen',en: 'Settings',   roles: ['owner','admin'] },
  ],
};

function getVisibleSubTabs(section, role, hrRole, industry) {
  return (SECTION_TABS[section] || []).filter(t => {
    // Industry-specific tabs
    if (t.industry && t.industry !== industry) return false;
    if (!role) return true;
    if (t.roles.includes(role)) return true;
    if (hrRole && t.roles.includes(hrRole)) return true;
    return false;
  });
}

function getDefaultSubTab(section, role, hrRole) {
  const tabs = getVisibleSubTabs(section, role, hrRole);
  return tabs[0]?.id || null;
}

export default function BusinessScreen({ lang, currentUserId, userName, onBack, businessId: propBusinessId, setBusinessId, license, businesses, onSwitchBusiness, onBusinessCreated, onOpenChat, onBizLoaded }) {
  const nl = lang === 'nl';
  const [navSection, setNavSection]   = useState('home');
  const [bizTab, setBizTab]           = useState('dashboard');
  const [business, setBusiness]       = useState(null);
  const [role, setRole]               = useState(null);
  const [loading, setLoading]         = useState(true);
  // Use propBusinessId directly — no local copy, no sync effect.
  // When the parent switches businessId, this component re-renders immediately.
  const businessId = propBusinessId;
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm]       = useState({});
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null); // member profile overlay
  const [showBizTour, setShowBizTour] = useState(false);
  const [loadError, setLoadError]     = useState(false);
  const [retryCount, setRetryCount]   = useState(0);

  const [hrRole, setHrRole] = useState(null); // accountant | bookkeeper | hr_manager
  const [showBizSwitcher, setShowBizSwitcher] = useState(false);

  const isOwner = role === 'owner';
  // Effective role for section visibility: hr_role takes precedence for HR section
  const effectiveRole = role;

  const switchSection = (section) => {
    setNavSection(section);
    const defaultTab = getDefaultSubTab(section, role, hrRole);
    if (defaultTab) setBizTab(defaultTab);
    setSelectedMember(null);
  };

  useEffect(() => {
    if (!businessId) {
      // No business ID from parent — nothing to load
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    setBusiness(null);
    getBusiness(businessId).then(b => {
      if (b && !b.error) {
        setBusiness(b);
        if (onBizLoaded) onBizLoaded(b);
        // Determine role: owner check takes priority over member row
        const biz = businesses?.find(x => x.id === businessId);
        const resolvedRole = b.owner_id === currentUserId ? 'owner' : (biz?.role || b.role || 'member');
        setRole(resolvedRole);
        setHrRole(biz?.hr_role || b.hr_role || null);
      } else {
        setLoadError(true);
      }
      setLoading(false);
    }).catch(() => {
      setLoadError(true);
      setLoading(false);
    });
  }, [businessId, currentUserId, retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = (biz) => {
    setBusiness(biz);
    if (setBusinessId) setBusinessId(biz.id);
    if (onBizLoaded) onBizLoaded(biz);
    setRole('owner');
    setBizTab('dashboard');
    setLoading(false);
    // Persist new active business to DB so refresh restores the correct one
    fetch(`${BASE}/businesses/activate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, business_id: biz.id }),
    }).catch(() => {});
    // Show business intro tour for first-time business creators
    if (!localStorage.getItem(BIZ_TOUR_KEY)) setTimeout(() => setShowBizTour(true), 600);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    const updated = await updateBusiness(businessId, editForm);
    if (updated && !updated.error) {
      const merged = { ...business, ...editForm };
      setBusiness(merged);
      if (onBizLoaded) onBizLoaded(merged);
    }
    setSaving(false);
    setEditingProfile(false);
  };

  const handleDeleteBusiness = async () => {
    if (!propBusinessId) return;
    setDeleting(true);
    try {
      await deleteBusiness(propBusinessId);
      if (setBusinessId) setBusinessId(null);
      setEditingProfile(false);
      setConfirmDelete(false);
      setBusiness(null);
      // Switch to remaining business or go back to personal
      const remaining = (businesses || []).filter(b => b.id !== propBusinessId);
      if (remaining.length > 0 && onSwitchBusiness) {
        onSwitchBusiness(remaining[0]);
      } else if (onBack) {
        onBack();
      }
    } catch {
      setDeleting(false);
    }
  };

  if (loading && !business) return (
    <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>
      <TopBar nl={nl} onBack={onBack} name={nl ? 'Laden...' : 'Loading...'} />
      <div style={{ flex: 1, padding: 16 }}><TechLoadingBar lang={lang} /></div>
    </div>
  );

  // API failed but we have a stored businessId — show retry instead of setup screen
  if (!business && loadError) {
    return (
      <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
        <style>{CSS}</style>
        <TopBar nl={nl} onBack={onBack} name={business?.name || 'Business'} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
              {nl ? 'Verbinding mislukt' : 'Connection failed'}
            </div>
            <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
              {nl ? 'Kon bedrijfsdata niet laden. Controleer je verbinding.' : 'Could not load business data. Check your connection.'}
            </div>
            <button onClick={() => setRetryCount(c => c + 1)}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '12px 24px', cursor: 'pointer' }}>
              🔄 {nl ? 'Opnieuw proberen' : 'Try again'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No business yet — show setup or pending message
  if (!business) {
    if (license !== 'companies') {
      return (
        <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
          <style>{CSS}</style>
          <TopBar nl={nl} onBack={onBack} name="Business" />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
              <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
                {nl ? 'Companies licentie vereist' : 'Companies license required'}
              </div>
              <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.6 }}>
                {nl ? 'Upgrade naar Companies om een bedrijfsprofiel aan te maken.' : 'Upgrade to Companies to create a business profile.'}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
        <style>{CSS}</style>
        <TopBar nl={nl} onBack={onBack} name={nl ? 'Nieuw bedrijf' : 'New business'} />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <BusinessSetupScreen lang={lang} currentUserId={currentUserId} onCreated={handleCreated} />
        </div>
      </div>
    );
  }

  // Edit profile overlay
  if (editingProfile) {
    return (
      <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
        <style>{CSS}</style>
        <TopBar nl={nl} onBack={() => { setEditingProfile(false); setConfirmDelete(false); }} name={nl ? 'Profiel bewerken' : 'Edit profile'} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <EditProfileForm biz={business} form={editForm} setForm={setEditForm} nl={nl} onSave={handleSaveProfile} onCancel={() => { setEditingProfile(false); setConfirmDelete(false); }} saving={saving} />

          {/* ── Danger Zone (owner only) ── */}
          <div style={{ marginTop: 32, borderTop: `1px solid #C0505033`, paddingTop: 20 }}>
            <div style={{ color: '#C05050', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
              {nl ? 'Gevarenzone' : 'Danger Zone'}
            </div>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  width: '100%', background: '#C0505015', border: '1px solid #C0505055',
                  borderRadius: 12, padding: '12px 16px', cursor: 'pointer',
                  color: '#C05050', fontSize: 13, fontWeight: 600, textAlign: 'left',
                }}
              >
                🗑 {nl ? 'Bedrijf verwijderen' : 'Delete business'}
              </button>
            ) : (
              <div style={{ background: '#C0505018', border: '1px solid #C0505055', borderRadius: 12, padding: 16 }}>
                <div style={{ color: '#C05050', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                  {nl ? 'Weet je het zeker?' : 'Are you sure?'}
                </div>
                <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
                  {nl
                    ? `Dit verwijdert "${business?.name}" permanent — inclusief alle leden, agenda, taken en projecten. Dit kan niet ongedaan worden.`
                    : `This permanently deletes "${business?.name}" — including all members, agenda, tasks and projects. This cannot be undone.`}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleDeleteBusiness}
                    disabled={deleting}
                    style={{
                      flex: 1, background: '#C05050', border: 'none',
                      borderRadius: 10, padding: '10px', cursor: 'pointer',
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      opacity: deleting ? 0.6 : 1,
                    }}
                  >
                    {deleting ? '...' : (nl ? 'Ja, verwijder' : 'Yes, delete')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    style={{
                      flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`,
                      borderRadius: 10, padding: '10px', cursor: 'pointer',
                      color: C.silver, fontSize: 13,
                    }}
                  >
                    {nl ? 'Annuleren' : 'Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Member profile overlay
  if (selectedMember) return (
    <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>
      <BusinessMemberProfileScreen
        member={selectedMember}
        businessId={businessId}
        lang={lang}
        currentUserId={currentUserId}
        myRole={role}
        onBack={() => setSelectedMember(null)}
        onChat={(member) => {
          setSelectedMember(null);
          if (onOpenChat) onOpenChat(member);
        }}
      />
    </div>
  );

  const bizIndustry = business?.industry || 'other';
  const subTabs = getVisibleSubTabs(navSection, role, hrRole, bizIndustry);

  return (
    <div style={{ height: '100%', background: _T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{CSS}</style>
      <TopBar nl={nl} onBack={onBack} name={business.name} logoUrl={business.logo_url}
        businesses={businesses} currentBizId={businessId}
        onSwitchBusiness={biz => { if (onSwitchBusiness) onSwitchBusiness(biz); }}
        onNewBusiness={isOwner || (license === 'companies' || license?.startsWith('coaches')) ? () => { setBizTab('setup_new'); } : null}
        showBizSwitcher={showBizSwitcher} setShowBizSwitcher={setShowBizSwitcher}
      />

      {/* Top navigation bar — below the top bar row */}
      <div style={{ flexShrink: 0, background: `${_T.bg2}f0`, borderBottom: `1px solid ${_T.cardBorderTop}`, display: 'flex', backdropFilter: 'blur(16px)', paddingTop: 2 }}>
        {BOTTOM_NAV.map(nav => {
          const isActive = navSection === nav.id;
          const accessible = getVisibleSubTabs(nav.id, role, hrRole, bizIndustry).length > 0;
          if (!accessible) return null;
          return (
            <button key={nav.id} onClick={() => switchSection(nav.id)}
              style={{ flex: 1, background: 'none', border: 'none', padding: '8px 4px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}>
              <span style={{ fontSize: 18, filter: isActive ? 'none' : 'grayscale(0.5)', opacity: isActive ? 1 : 0.5 }}>{nav.icon}</span>
              <span style={{ fontSize: 9, color: isActive ? _T.accent : C.silver2, fontWeight: isActive ? 700 : 400 }}>{nl ? nav.nl : nav.en}</span>
              {isActive && <div style={{ position: 'absolute', bottom: 0, left: '20%', right: '20%', height: 2, borderRadius: '2px 2px 0 0', background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})` }} />}
            </button>
          );
        })}
      </div>

      {/* Sub-tab strip — only when section has multiple tabs */}
      {subTabs.length > 1 && (
        <div style={{ display: 'flex', background: _T.bg2, borderBottom: `1px solid ${_T.cardBorderTop}`, padding: '0 8px', gap: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {subTabs.map(t => (
            <button key={t.id} onClick={() => { playSound('tab'); setBizTab(t.id); }}
              style={{ background: 'none', border: 'none', borderBottom: `2px solid ${bizTab === t.id ? _T.accent : 'transparent'}`, padding: '10px 12px 8px', cursor: 'pointer', color: bizTab === t.id ? _T.accent : C.silver, fontSize: 12, fontWeight: bizTab === t.id ? 700 : 400, whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
              {nl ? t.nl : t.en}
            </button>
          ))}
        </div>
      )}

      {/* Content area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 24px', minHeight: 0 }}>
        {bizTab === 'manual'    && <BusinessManualScreen lang={lang} />}
        {bizTab === 'dashboard' && <BusinessDashboard lang={lang} businessId={businessId} business={business} currentUserId={currentUserId} role={role} onSwitchTab={id => { setBizTab(id); const sec = Object.entries(SECTION_TABS).find(([,tabs]) => tabs.some(t => t.id === id)); if (sec) setNavSection(sec[0]); }} />}
        {bizTab === 'members'   && <BusinessMembersScreen lang={lang} businessId={businessId} bizName={business?.name} currentUserId={currentUserId} isOwner={isOwner} role={role} onMemberClick={setSelectedMember} />}
        {bizTab === 'customers' && <BusinessCustomersScreen lang={lang} businessId={businessId} role={role} onCreateBooking={() => { setBizTab('bookings'); setNavSection('clients'); }} />}
        {bizTab === 'bookings'  && <BusinessBookingsScreen lang={lang} businessId={businessId} role={role} onNav={setBizTab} />}
        {bizTab === 'services'  && <BusinessServicesScreen lang={lang} businessId={businessId} role={role} onOpenBookings={() => { setBizTab('bookings'); setNavSection('clients'); }} />}
        {bizTab === 'products'  && <BusinessProductsScreen lang={lang} businessId={businessId} role={role} business={business} />}
        {bizTab === 'orders'    && <BusinessOrdersScreen lang={lang} businessId={businessId} role={role} />}
        {bizTab === 'licenses'  && <BusinessLicensePackagesScreen lang={lang} businessId={businessId} role={role} />}
        {bizTab === 'bizapi'    && <BusinessApiScreen lang={lang} businessId={businessId} role={role} />}
        {bizTab === 'widget'    && <BusinessBookingWidget lang={lang} businessId={businessId} role={role} />}
        {bizTab === 'import'    && <BusinessImportScreen lang={lang} businessId={businessId} role={role} />}
        {bizTab === 'administration' && <BusinessAdministrationScreen lang={lang} businessId={businessId} business={business} currentUserId={currentUserId} role={role} />}
        {bizTab === 'transfer'  && isOwner && <BusinessOwnershipTransferScreen lang={lang} businessId={businessId} business={business} currentUserId={currentUserId} userName={userName} />}
        {bizTab === 'tasks'     && <BusinessTasksScreen lang={lang} businessId={businessId} currentUserId={currentUserId} role={role} />}
        {bizTab === 'projects'  && <BusinessProjectsScreen lang={lang} businessId={businessId} currentUserId={currentUserId} role={role} />}
        {bizTab === 'pulse'     && <BusinessPulseScreen lang={lang} businessId={businessId} currentUserId={currentUserId} isOwner={isOwner} />}
        {bizTab === 'reports'   && <BusinessReportsScreen lang={lang} businessId={businessId} isOwner={isOwner} />}
        {bizTab === 'programs'  && <BusinessProgramsScreen lang={lang} businessId={businessId} currentUserId={currentUserId} role={role} />}
        {bizTab === 'training'  && <BusinessTrainingScreen lang={lang} currentUserId={currentUserId} businessId={businessId} />}
        {bizTab === 'activity'  && <BusinessActivityScreen lang={lang} businessId={businessId} currentUserId={currentUserId} />}
        {bizTab === 'roi'       && <BusinessROIScreen lang={lang} businessId={businessId} />}
        {bizTab === 'rie'       && <BusinessRIEScreen lang={lang} businessId={businessId} currentUserId={currentUserId} role={role} />}
        {bizTab === 'agenda'    && <BusinessAgendaScreen lang={lang} businessId={businessId} currentUserId={currentUserId} role={role} isOwner={isOwner} />}
        {bizTab === 'setup_new' && (
          <BusinessSetupScreen lang={lang} currentUserId={currentUserId} onCreated={biz => {
            if (onBusinessCreated) onBusinessCreated(biz);
            setBizTab('dashboard');
          }} />
        )}
        {bizTab === 'settings'  && isOwner && (
          <>
            <BusinessSettingsScreen lang={lang} businessId={businessId} />
            <div style={{ marginTop: 16 }}>
              <button onClick={() => { localStorage.removeItem(BIZ_TOUR_KEY); setShowBizTour(true); }}
                style={{ width: '100%', background: `${_T.accent}15`, border: `1px solid ${_T.accent}33`, borderRadius: 12, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '12px', cursor: 'pointer' }}>
                🎓 {nl ? 'Dashboard rondleiding opnieuw starten' : 'Restart dashboard tour'}
              </button>
            </div>
            {/* Danger Zone — owner only */}
            <div style={{ marginTop: 32, borderTop: '1px solid #C0505033', paddingTop: 20 }}>
              <div style={{ color: '#C05050', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
                {nl ? 'Gevarenzone' : 'Danger Zone'}
              </div>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{ width: '100%', background: '#C0505015', border: '1px solid #C0505055', borderRadius: 12, padding: '12px 16px', cursor: 'pointer', color: '#C05050', fontSize: 13, fontWeight: 600, textAlign: 'left' }}
                >
                  🗑 {nl ? 'Bedrijf verwijderen' : 'Delete business'}
                </button>
              ) : (
                <div style={{ background: '#C0505015', border: '1px solid #C0505055', borderRadius: 12, padding: 16 }}>
                  <div style={{ color: '#C05050', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                    {nl ? 'Weet je het zeker?' : 'Are you sure?'}
                  </div>
                  <div style={{ color: '#C05050', fontSize: 12, marginBottom: 16, opacity: 0.8 }}>
                    {nl ? 'Dit verwijdert het bedrijf en alle bijbehorende data permanent.' : 'This permanently deletes the business and all its data.'}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, background: 'transparent', border: '1px solid #ffffff22', borderRadius: 10, padding: '10px', cursor: 'pointer', color: '#ffffff88', fontSize: 13 }}>
                      {nl ? 'Annuleren' : 'Cancel'}
                    </button>
                    <button onClick={handleDeleteBusiness} disabled={deleting} style={{ flex: 1, background: '#C05050', border: 'none', borderRadius: 10, padding: '10px', cursor: deleting ? 'not-allowed' : 'pointer', color: '#fff', fontSize: 13, fontWeight: 700, opacity: deleting ? 0.6 : 1 }}>
                      {deleting ? '⏳' : (nl ? 'Verwijderen' : 'Delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {['contracts','leave','sick','balance','hrroles'].includes(bizTab) && (
          <BusinessHRScreen businessId={businessId} myRole={hrRole || role} myUid={currentUserId} activeTab={bizTab} lang={lang} />
        )}
        {bizTab === 'feed' && (
          <BusinessFeedScreen businessId={businessId} myRole={hrRole || role} myUid={currentUserId} lang={lang} />
        )}
        {bizTab === 'profile'   && (
          <BusinessProfileScreen lang={lang} businessId={businessId} isOwner={isOwner}
            onEdit={isOwner ? () => { setEditForm({ name: business.name, logo_url: business.logo_url || '', industry: business.industry || 'other', address: business.address || '', description: business.description || '', opening_hours: business.opening_hours || {} }); setEditingProfile(true); } : null}
          />
        )}
      </div>

      {showBizTour && <BusinessIntroModal lang={lang} onClose={() => setShowBizTour(false)} />}
    </div>
  );
}

function TopBar({ nl, onBack, name, logoUrl, businesses, currentBizId, onSwitchBusiness, onNewBusiness, showBizSwitcher, setShowBizSwitcher }) {
  return (
    <div style={{ background: _T.bg2, borderBottom: `1px solid ${_T.cardBorderTop}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 50, backdropFilter: 'blur(16px)' }}>
      <button onClick={onBack} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', fontSize: 18, color: C.silver, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>‹</button>
      {logoUrl && <img src={logoUrl} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}

      {/* Business name — clickable to open switcher if multiple businesses */}
      <button onClick={() => (businesses && businesses.length > 1) ? setShowBizSwitcher(v => !v) : null}
        style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: C.white, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'none', border: 'none', textAlign: 'left', cursor: (businesses && businesses.length > 1) ? 'pointer' : 'default', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        {name}
        {businesses && businesses.length > 1 && <span style={{ fontSize: 10, color: C.silver2 }}>▾</span>}
      </button>

      {/* New business button */}
      {onNewBusiness && (
        <button onClick={onNewBusiness}
          style={{ flexShrink: 0, background: `${_T.accent}18`, border: `1px solid ${_T.accent}44`, borderRadius: 10, color: _T.accent, fontSize: 11, fontWeight: 700, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + {nl ? 'Nieuw' : 'New'}
        </button>
      )}

      {/* Business switcher dropdown */}
      {showBizSwitcher && businesses && businesses.length > 1 && (
        <>
          <div onClick={() => setShowBizSwitcher(false)} style={{ position: 'fixed', inset: 0, zIndex: 98 }} />
          <div style={{ position: 'absolute', top: 58, left: 60, zIndex: 99, background: _T.bg2, border: `1px solid ${_T.cardBorderTop}`, borderRadius: 14, padding: 6, minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 9, color: C.silver2, letterSpacing: 2, fontWeight: 700, padding: '4px 10px 6px' }}>{nl ? 'WISSEL VAN BEDRIJF' : 'SWITCH BUSINESS'}</div>
            {businesses.map(biz => (
              <button key={biz.id} onClick={() => { onSwitchBusiness(biz); setShowBizSwitcher(false); }}
                style={{ width: '100%', background: biz.id === currentBizId ? `${_T.accent}15` : 'transparent', border: 'none', borderRadius: 10, padding: '9px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, overflow: 'hidden', background: _T.bg4, border: `1.5px solid ${biz.id === currentBizId ? _T.accent : _T.cardBorderBot}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {biz.logo_url ? <img src={biz.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : <span style={{ fontSize: 14 }}>🏢</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: biz.id === currentBizId ? _T.accent : C.white, fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{biz.name}</div>
                  <div style={{ color: C.silver2, fontSize: 10 }}>{biz.role === 'owner' ? (nl ? 'Eigenaar' : 'Owner') : (nl ? 'Lid' : 'Member')}</div>
                </div>
                {biz.id === currentBizId && <div style={{ width: 6, height: 6, borderRadius: '50%', background: _T.accent }} />}
              </button>
            ))}
            <div style={{ height: 1, background: _T.cardBorderBot, margin: '4px 8px' }} />
            {onNewBusiness && (
              <button onClick={() => { onNewBusiness(); setShowBizSwitcher(false); }}
                style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: 10, padding: '9px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, color: _T.accent, fontSize: 12, fontWeight: 600 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: `${_T.accent}18`, border: `1.5px solid ${_T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>+</div>
                {nl ? 'Nieuw bedrijf aanmaken' : 'Create new business'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const DAYS_EDIT = [
  { key: 'mon', nl: 'Maandag', en: 'Monday' }, { key: 'tue', nl: 'Dinsdag', en: 'Tuesday' },
  { key: 'wed', nl: 'Woensdag', en: 'Wednesday' }, { key: 'thu', nl: 'Donderdag', en: 'Thursday' },
  { key: 'fri', nl: 'Vrijdag', en: 'Friday' }, { key: 'sat', nl: 'Zaterdag', en: 'Saturday' },
  { key: 'sun', nl: 'Zondag', en: 'Sunday' },
];

function EditProfileForm({ biz, form, setForm, nl, onSave, onCancel, saving }) {
  const init = form.name !== undefined ? form : { name: biz.name || '', logo_url: biz.logo_url || '', industry: biz.industry || 'other', address: biz.address || '', description: biz.description || '', opening_hours: biz.opening_hours || {} };
  const [f, setF] = useState(init);
  const [logoPreview, setLogoPreview] = useState(biz.logo_url || null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef();
  useEffect(() => { setForm(f); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) {
        setF(p => ({ ...p, logo_url: data.url }));
      } else {
        setUploadError(data.error || (nl ? 'Upload mislukt.' : 'Upload failed.'));
        setLogoPreview(biz.logo_url || null);
      }
    } catch {
      setUploadError(nl ? 'Verbindingsfout.' : 'Connection error.');
      setLogoPreview(biz.logo_url || null);
    }
    setUploading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Business name */}
      <div>
        <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Bedrijfsnaam' : 'Business name'}</div>
        <input type="text" value={f.name || ''} onChange={e => setF(p => ({ ...p, name: e.target.value }))} style={s_TI({ width: '100%', boxSizing: 'border-box' })} />
      </div>
      {/* Logo upload */}
      <div>
        <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Logo' : 'Logo'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div onClick={() => fileRef.current?.click()}
            style={{ width: 56, height: 56, borderRadius: 12, background: logoPreview ? 'transparent' : _T.bg4, border: `2px dashed ${uploading ? _T.accent : _T.cardBorderBot}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
            {logoPreview
              ? <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 22, opacity: 0.4 }}>🏢</span>
            }
            {uploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 16 }}>⏳</span></div>}
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{ flex: 1, background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 10, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '9px 12px', cursor: uploading ? 'default' : 'pointer' }}>
            {uploading ? (nl ? 'Uploaden...' : 'Uploading...') : (logoPreview ? (nl ? 'Andere afbeelding' : 'Change image') : (nl ? 'Afbeelding kiezen' : 'Choose image'))}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
        </div>
        {uploadError && <div style={{ color: C.red, fontSize: 11, marginTop: 6 }}>⚠ {uploadError}</div>}
      </div>
      {/* Industry */}
      <div>
        <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Sector' : 'Industry'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
          {INDUSTRIES.map(ind => (
            <button key={ind.id} type="button" onClick={() => setF(p => ({ ...p, industry: ind.id }))}
              style={{ background: f.industry === ind.id ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${f.industry === ind.id ? _T.accent : _T.cardBorderBot}`, borderRadius: 12, padding: '8px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, transition: 'all 0.15s' }}>
              <span style={{ fontSize: 16 }}>{ind.icon}</span>
              <span style={{ fontSize: 9, color: f.industry === ind.id ? _T.accent : C.silver, fontWeight: f.industry === ind.id ? 700 : 400, textAlign: 'center', lineHeight: 1.2 }}>{nl ? ind.nl : ind.en}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Address */}
      {[['address', nl ? 'Adres' : 'Address', 'text']].map(([key, label, type]) => (
        <div key={key}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{label}</div>
          <input type={type} value={f[key] || ''} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))} style={s_TI({ width: '100%', boxSizing: 'border-box' })} />
        </div>
      ))}
      <div>
        <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Beschrijving' : 'Description'}</div>
        <textarea value={f.description || ''} onChange={e => setF(p => ({ ...p, description: e.target.value }))} rows={3}
          style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14 }}>
        <div style={{ color: C.silver, fontSize: 11, marginBottom: 10 }}>{nl ? 'Openingstijden' : 'Opening hours'}</div>
        {DAYS_EDIT.map(d => (
          <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ color: C.silver, fontSize: 12, width: 80, flexShrink: 0 }}>{nl ? d.nl : d.en}</div>
            <input type="text" value={(f.opening_hours || {})[d.key] || ''} onChange={e => setF(p => ({ ...p, opening_hours: { ...(p.opening_hours || {}), [d.key]: e.target.value } }))}
              placeholder="09:00-17:00" style={{ ...s_TI({ flex: 1 }), fontSize: 12, padding: '7px 10px' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 14, padding: '12px', cursor: 'pointer' }}>{nl ? 'Annuleren' : 'Cancel'}</button>
        <button onClick={onSave} disabled={saving || uploading} style={{ flex: 1, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '12px', cursor: (saving || uploading) ? 'default' : 'pointer' }}>
          {saving ? '...' : (nl ? 'Opslaan' : 'Save')}
        </button>
      </div>
    </div>
  );
}
